import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useForm } from "react-hook-form";

import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";
import Guide from "@/components/Guide";
import Table from "@/components/Table";

import CommentsModal from "@/modals/CommentsModal";
import CsListModal from "@/modals/CsListModal";
import DeliveryModal from "@/modals/DeliveryModal";

import { COMMENT_MENU_TYPE } from "@/stores/DeliveryComments";
import {
  QUERY_SORT,
  REVIEW_FILTER,
  formatNextAutoDisposalAt,
} from "@/stores/LostDeliveries";
import {
  useAuthStore,
  useDeliveryCommentsStore,
  useLoadingStore,
  useLostDeliveriesStore,
  useModalStore,
  useProfileStore,
} from "@/stores/hooks";

const BodyContent = styled.div``;
const Container = styled.div`
  width: 100%;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 40px;
`;

const SearchWrap = styled.div`
  padding-top: 15px;
`;
const SearchRow = styled(Row)`
  margin-bottom: 10px;
`;
const Column = styled(Col)`
  display: flex;
  align-items: center;
`;
const ColumnRight = styled(Column)`
  justify-content: flex-end;
`;
const SearchButton = styled(Button)`
  margin-left: 5px;
`;
const ActionButton = styled(Button)`
  margin: 0;
`;
const ActionCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`;
const FormControl = styled(Form.Control)`
  width: ${({ width }) => width || 180}px !important;
`;
const Wave = styled.span`
  margin: 0 5px;
`;
const SearchLabel = styled.div`
  font-weight: bold;
  margin-right: 10px;
`;
const TabWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;
const TabLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  font-weight: ${({ active }) => (active ? "bold" : "normal")};
  cursor: pointer;
`;
const TableWrap = styled.div``;
const ColumnLink = styled.div`
  cursor: pointer;
  text-decoration: underline;
`;

const SORT_DATE = "date";

const SORT_OPTIONS = [
  { value: SORT_DATE, label: "분실완료일자" },
  { value: QUERY_SORT.UNCONFIRMED_COMMENT, label: "미확인코멘트" },
  { value: QUERY_SORT.COMMENT_CREATED_AT, label: "코멘트" },
];

const FILTER_TABS = [
  {
    id: "all",
    label: "전체",
    query: {
      review: undefined,
    },
  },
  {
    id: "pending",
    label: "폐기승인/반송승인 전",
    query: {
      review: REVIEW_FILTER.PENDING,
    },
  },
  {
    id: "disposalApproved",
    label: "파손폐기승인",
    query: {
      review: REVIEW_FILTER.DISPOSAL_APPROVED,
    },
  },
  {
    id: "returnApproved",
    label: "파손반송승인",
    query: {
      review: REVIEW_FILTER.RETURN_APPROVED,
    },
  },
];

const getDateSortByReview = (review) => {
  if (review === REVIEW_FILTER.DISPOSAL_APPROVED) {
    return QUERY_SORT.DISPOSAL_APPROVED_AT;
  }

  if (review === REVIEW_FILTER.RETURN_APPROVED) {
    return QUERY_SORT.RETURN_APPROVED_AT;
  }

  return QUERY_SORT.LOST_COMPLETED_AT;
};

const getSortDropdownValue = (sort) => {
  if (sort === QUERY_SORT.UNCONFIRMED_COMMENT) {
    return QUERY_SORT.UNCONFIRMED_COMMENT;
  }

  if (sort === QUERY_SORT.COMMENT_CREATED_AT) {
    return QUERY_SORT.COMMENT_CREATED_AT;
  }

  return SORT_DATE;
};

const getActiveTabId = (query) => {
  if (query.review === REVIEW_FILTER.PENDING) {
    return "pending";
  }

  if (query.review === REVIEW_FILTER.DISPOSAL_APPROVED) {
    return "disposalApproved";
  }

  if (query.review === REVIEW_FILTER.RETURN_APPROVED) {
    return "returnApproved";
  }

  return "all";
};

const mapSearchKeyword = (value) => {
  const keyword = (value || "").trim();

  if (!keyword) {
    return { bookId: "", receiverName: "", receiverMobile: "" };
  }

  const digits = keyword.replace(/[-\s]/g, "");

  if (/^01\d{8,9}$/.test(digits) || /^0\d{9,10}$/.test(digits)) {
    return { bookId: "", receiverName: "", receiverMobile: keyword };
  }

  if (/^\d+$/.test(digits) && digits.length >= 8) {
    return { bookId: keyword, receiverName: "", receiverMobile: "" };
  }

  return { bookId: "", receiverName: keyword, receiverMobile: "" };
};

const BookIdColumn = memo(({ bookId, onAfterSubmit }) => {
  const { openModal } = useModalStore();

  const handleClick = useCallback(async () => {
    if (!bookId) {
      return;
    }

    openModal(
      <DeliveryModal
        key={`lost-delivery-${bookId}-modal`}
        bookId={bookId}
        onAfterSubmit={onAfterSubmit}
      />,
    );
  }, [bookId, onAfterSubmit, openModal]);

  return <ColumnLink onClick={handleClick}>{bookId}</ColumnLink>;
});

const ProcessColumn = memo(({ row, onAfterSubmit }) => {
  const { returnApprove, disposalApprove, disposalWait } =
    useLostDeliveriesStore();
  const { create } = useDeliveryCommentsStore();
  const { startLoading, finishLoading } = useLoadingStore();

  const postComment = useCallback(
    async (bookId, content) => {
      try {
        await create(bookId, {
          menuType: COMMENT_MENU_TYPE.LOST_DAMAGED,
          content,
        });
      } catch (_e) {
        window.alert("처리는 되었으나 코멘트 자동 작성에 실패했습니다.");
      }
    },
    [create],
  );

  const handleReturnApprove = useCallback(async () => {
    const bookId = row.renderBookId();

    if (
      !window.confirm(
        `${row.renderReceiptLabel()} 접수건을 반송승인 처리하시겠습니까?`,
      )
    ) {
      return;
    }

    try {
      startLoading();
      await returnApprove(bookId);
      await postComment(
        bookId,
        `${row.renderReceiptLabel()} 접수건을 반송승인 처리했습니다.`,
      );
      window.alert("반송승인 처리되었습니다.");

      if (onAfterSubmit) {
        await onAfterSubmit();
      }
    } catch (e) {
      window.alert(`반송승인에 실패했습니다.\n${e.message}`);
    }

    finishLoading();
  }, [
    finishLoading,
    onAfterSubmit,
    postComment,
    returnApprove,
    row,
    startLoading,
  ]);

  const handleDisposalApprove = useCallback(async () => {
    const bookId = row.renderBookId();

    if (
      !window.confirm(
        `${row.renderReceiptLabel()} 접수건을 폐기승인 처리하시겠습니까?`,
      )
    ) {
      return;
    }

    try {
      startLoading();
      await disposalApprove(bookId);
      await postComment(
        bookId,
        `${row.renderReceiptLabel()} 접수건을 폐기승인 처리했습니다.`,
      );
      window.alert("폐기승인 처리되었습니다.");

      if (onAfterSubmit) {
        await onAfterSubmit();
      }
    } catch (e) {
      window.alert(`폐기승인에 실패했습니다.\n${e.message}`);
    }

    finishLoading();
  }, [
    disposalApprove,
    finishLoading,
    onAfterSubmit,
    postComment,
    row,
    startLoading,
  ]);

  const handleDisposalWait = useCallback(async () => {
    const bookId = row.renderBookId();
    const nextAt = formatNextAutoDisposalAt();

    if (
      !window.confirm(
        `${row.renderReceiptLabel()} 접수건의 자동 폐기승인 일시를 ${nextAt} 으로 변경하시겠습니까?`,
      )
    ) {
      return;
    }

    try {
      startLoading();
      await disposalWait(bookId);
      window.alert("폐기대기로 처리되었습니다.");

      if (onAfterSubmit) {
        await onAfterSubmit();
      }
    } catch (e) {
      window.alert(`폐기대기에 실패했습니다.\n${e.message}`);
    }

    finishLoading();
  }, [disposalWait, finishLoading, onAfterSubmit, row, startLoading]);

  return (
    <ActionCell>
      {row.canApproveDisposal() && (
        <ActionButton
          type="button"
          width="80px"
          onClick={handleDisposalApprove}
        >
          폐기승인
        </ActionButton>
      )}
      {row.canWaitDisposal() && (
        <ActionButton type="button" width="80px" onClick={handleDisposalWait}>
          폐기대기
        </ActionButton>
      )}
      {row.canApproveReturn() && (
        <ActionButton type="button" width="80px" onClick={handleReturnApprove}>
          반송승인
        </ActionButton>
      )}
    </ActionCell>
  );
});

const CsColumn = memo(({ row }) => {
  const { openModal } = useModalStore();

  const handleOpenCsList = useCallback(() => {
    const bookId = row.renderBookId();

    if (!bookId) {
      window.alert("송장번호가 없어 CS를 조회할 수 없습니다.");
      return;
    }

    openModal(<CsListModal key={`lost-cs-list-${bookId}`} bookId={bookId} />);
  }, [openModal, row]);

  return (
    <ActionCell>
      <ActionButton type="button" width="70px" onClick={handleOpenCsList}>
        CS보기
      </ActionButton>
    </ActionCell>
  );
});

const CommentsColumn = memo(({ row }) => {
  const { openModal } = useModalStore();

  const handleOpenComments = useCallback(() => {
    const bookId = row.renderBookId();

    if (!bookId) {
      window.alert("송장번호가 없어 코멘트를 조회할 수 없습니다.");
      return;
    }

    openModal(
      <CommentsModal
        key={`lost-comments-${bookId}`}
        bookId={bookId}
        menuType={COMMENT_MENU_TYPE.LOST_DAMAGED}
      />,
    );
  }, [openModal, row]);

  return (
    <ActionCell>
      <ActionButton type="button" width="70px" onClick={handleOpenComments}>
        코멘트
      </ActionButton>
    </ActionCell>
  );
});

export default function LostDeliveries() {
  const { state, ...actions } = useLostDeliveriesStore();
  const { user: authUser } = useAuthStore().state;
  const { user: profileUser } = useProfileStore().state;
  const canUseHanjinCsApproval = Boolean(
    authUser?.canUseHanjinCsApproval || profileUser?.canUseHanjinCsApproval,
  );
  const { startLoading, finishLoading } = useLoadingStore();
  const { handleSubmit } = useForm();
  const [keyword, setKeyword] = useState("");

  const fetchAll = useCallback(
    async (queryOverride) => {
      try {
        startLoading();
        await actions.fetchAll(queryOverride);
      } catch (e) {
        window.alert(`분실파손 내역 조회에 실패했습니다.\n${e.message}`);
      }

      finishLoading();
    },
    [actions.fetchAll, finishLoading, startLoading],
  );

  useEffect(() => {
    fetchAll();
  }, [
    state.query.page,
    state.query.pageSize,
    state.query.sort,
    state.query.review,
  ]);

  const handleChangeDate = useCallback(
    (e) => {
      actions.setQuery({ [e.target.name]: e.target.value });

      if (
        e.target.name === "lostCompletedAtFrom" &&
        e.target.value > state.query.lostCompletedAtTo
      ) {
        actions.setQuery({ lostCompletedAtTo: e.target.value });
      }

      if (
        e.target.name === "lostCompletedAtTo" &&
        e.target.value < state.query.lostCompletedAtFrom
      ) {
        actions.setQuery({ lostCompletedAtFrom: e.target.value });
      }
    },
    [actions.setQuery, state.query],
  );

  const handleChangeKeyword = useCallback((e) => {
    setKeyword(e.target.value);
  }, []);

  const onSubmit = useCallback(() => {
    const nextQuery = {
      ...state.query,
      page: 1,
      ...mapSearchKeyword(keyword),
    };

    actions.setQuery(nextQuery);
    fetchAll(nextQuery);
  }, [actions.setQuery, fetchAll, keyword, state.query]);

  const handleSelectTab = useCallback(
    (tab) => {
      const nextReview = tab.query.review;
      const dropdownSort = getSortDropdownValue(state.query.sort);
      const nextSort =
        dropdownSort === SORT_DATE
          ? getDateSortByReview(nextReview)
          : dropdownSort;

      actions.setQuery({
        page: 1,
        review: nextReview,
        sort: nextSort,
      });
    },
    [actions.setQuery, state.query.sort],
  );

  const handleChangeSort = useCallback(
    (e) => {
      const value = e.target.value;
      const sort =
        value === SORT_DATE ? getDateSortByReview(state.query.review) : value;

      actions.setQuery({
        page: 1,
        sort,
      });
    },
    [actions.setQuery, state.query.review],
  );

  const handleGoToPage = useCallback(
    (page) => {
      actions.setQuery({ page });
    },
    [actions.setQuery],
  );

  const handleNextPage = useCallback(() => {
    actions.setQuery({ page: state.query.page + 1 });
  }, [actions.setQuery, state.query.page]);

  const handlePreviousPage = useCallback(() => {
    actions.setQuery({ page: state.query.page - 1 });
  }, [actions.setQuery, state.query.page]);

  const handleSelect = useCallback(() => {}, []);

  const activeTabId = getActiveTabId(state.query);

  const visibleFilterTabs = useMemo(
    () =>
      canUseHanjinCsApproval
        ? FILTER_TABS
        : FILTER_TABS.filter((tab) => tab.id === "all"),
    [canUseHanjinCsApproval],
  );

  const columns = useMemo(
    () =>
      [
      {
        Header: "배송상태",
        accessor: (row) => row.renderDeliveryStatus(),
        width: 90,
      },
      {
        Header: "송장번호",
        accessor: (row) => (
          <BookIdColumn bookId={row.renderBookId()} onAfterSubmit={fetchAll} />
        ),
        width: 110,
      },
      {
        Header: "주문번호",
        accessor: (row) => row.renderOrderIdFromCorp(),
        width: 130,
      },
      {
        Header: "분실/파손사유",
        accessor: (row) => row.renderLostReason(),
        width: 120,
      },
      {
        Header: "분실/파손완료일시",
        accessor: (row) => row.renderLostCompletedAt(),
        width: 140,
      },
      {
        Header: "최근코멘트작성자",
        accessor: (row) => row.renderLatestCommentWriterName(),
        width: 120,
      },
      {
        Header: "최근코멘트작성일시",
        accessor: (row) => row.renderLatestCommentCreatedAt(),
        width: 140,
      },
      {
        Header: "코멘트확인여부",
        accessor: (row) => row.renderCommentConfirmed(),
        width: 110,
      },
      {
        Header: "반송 승인 일시",
        id: "returnApprovedAt",
        accessor: (row) => row.renderReturnApprovedAt(),
        width: 180,
      },
      {
        Header: "폐기 승인 일시",
        id: "disposalApprovedAt",
        accessor: (row) => row.renderDisposeConfirmedAt(),
        width: 180,
      },
      {
        Header: "처리",
        id: "process",
        accessor: (row) => <ProcessColumn row={row} onAfterSubmit={fetchAll} />,
        width: 260,
      },
      {
        Header: "CS",
        accessor: (row) => <CsColumn row={row} />,
        width: 90,
      },
      {
        Header: "코멘트",
        accessor: (row) => (
          <CommentsColumn row={row} />
        ),
        width: 90,
      },
    ].filter(
      (column) =>
        canUseHanjinCsApproval ||
        (column.id !== "returnApprovedAt" &&
          column.id !== "disposalApprovedAt" &&
          column.id !== "process"),
    ),
    [canUseHanjinCsApproval, fetchAll],
  );

  return (
    <BodyContent>
      <Guide />
      <Container>
        <SearchWrap>
          <SearchRow>
            <Column xs={8}>
              <Form inline onSubmit={handleSubmit(onSubmit)}>
                <SearchLabel>분실완료일자</SearchLabel>
                <FormControl
                  name="lostCompletedAtFrom"
                  onChange={handleChangeDate}
                  type="date"
                  value={state.query.lostCompletedAtFrom}
                />
                <Wave>~</Wave>
                <FormControl
                  name="lostCompletedAtTo"
                  onChange={handleChangeDate}
                  type="date"
                  value={state.query.lostCompletedAtTo}
                />
                <SearchButton type="submit" width="100px">
                  조회
                </SearchButton>
              </Form>
            </Column>
            <ColumnRight xs={4}>
              <Form inline onSubmit={handleSubmit(onSubmit)}>
                <FormControl
                  width={260}
                  name="keyword"
                  onChange={handleChangeKeyword}
                  placeholder="송장번호, 수하인명, 연락처 조회"
                  value={keyword}
                />
                <SearchButton type="submit" width="100px">
                  조회
                </SearchButton>
              </Form>
            </ColumnRight>
          </SearchRow>
          <SearchRow>
            <Column xs={8}>
              <TabWrap>
                {visibleFilterTabs.map((tab) => (
                  <TabLabel key={tab.id} active={activeTabId === tab.id}>
                    <input
                      checked={activeTabId === tab.id}
                      name="lost-filter-tab"
                      onChange={() => handleSelectTab(tab)}
                      type="radio"
                    />
                    {tab.label}
                  </TabLabel>
                ))}
              </TabWrap>
            </Column>
            <ColumnRight xs={4}>
              <SearchLabel>전체 총 {state.totalCount} 조회</SearchLabel>
              <FormControl
                width={140}
                as="select"
                name="sort"
                onChange={handleChangeSort}
                value={getSortDropdownValue(state.query.sort)}
                custom
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FormControl>
            </ColumnRight>
          </SearchRow>
        </SearchWrap>
        <TableWrap>
          <Table
            responsive
            bordered
            hover
            columns={columns}
            currentPage={state.query.page}
            data={state.rows}
            goToPage={handleGoToPage}
            nextPage={handleNextPage}
            onSelect={handleSelect}
            pageCount={state.pageCount}
            pageSize={state.query.pageSize}
            previousPage={handlePreviousPage}
            showPageSize={false}
          />
        </TableWrap>
      </Container>
    </BodyContent>
  );
}
